import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { warehousesApi } from '../../../services/api'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

interface Warehouse {
  id: number
  name: string
  address: string | null
  is_active: boolean
  created_at: string
}

interface WarehouseForm {
  name: string
  address: string
}

const emptyForm: WarehouseForm = { name: '', address: '' }

export default function WarehousesTab() {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<WarehouseForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [togglingId, setTogglingId] = useState<number | null>(null)

  const fetchWarehouses = async () => {
    try {
      setLoadError('')
      const res = await warehousesApi.list()
      setWarehouses(res.data.warehouses || [])
    } catch (err) {
      console.error('Error fetching warehouses:', err)
      setLoadError('No se pudieron cargar los almacenes. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWarehouses()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (w: Warehouse) => {
    setForm({ name: w.name, address: w.address || '' })
    setEditingId(w.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = { name: form.name.trim(), address: form.address.trim() || null }
      if (editingId) {
        await warehousesApi.update(editingId, payload)
        toast.success('Almacén actualizado')
      } else {
        await warehousesApi.create(payload)
        toast.success('Almacén creado')
      }
      setShowForm(false)
      fetchWarehouses()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al guardar el almacén')
    } finally {
      setSaving(false)
    }
  }

  const handleToggleActive = async (w: Warehouse) => {
    setTogglingId(w.id)
    try {
      await warehousesApi.update(w.id, { is_active: !w.is_active })
      toast.success(w.is_active ? 'Almacén desactivado' : 'Almacén activado')
      fetchWarehouses()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al actualizar el almacén')
    } finally {
      setTogglingId(null)
    }
  }

  const { sortedData: sortedWarehouses, sortConfig, requestSort } = useSortableData(warehouses)

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-espresso-500 max-w-2xl">
          Almacenes físicos usados en cada etapa de producción y como destino de lotes en tránsito. No se pueden
          eliminar, solo desactivar.
        </p>
        <button
          onClick={openNew}
          className="shrink-0 ml-4 px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Almacén
        </button>
      </div>

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && warehouses.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay almacenes registrados aún. Crea el primero para poder registrar lotes.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Nombre" sortKey="name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Dirección" sortKey="address" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="is_active" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-6 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedWarehouses.map((w) => (
                  <tr key={w.id} className="hover:bg-cream-100">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{w.name}</td>
                    <td className="px-6 py-4 text-sm text-espresso-500">{w.address || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          w.is_active ? 'bg-green-100 text-green-800' : 'bg-cream-200 text-espresso-700'
                        }`}
                      >
                        {w.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(w)} variant="primary">
                          Editar
                        </TableActionButton>
                        <TableActionButton
                          onClick={() => handleToggleActive(w)}
                          variant={w.is_active ? 'danger' : 'success'}
                          disabled={togglingId === w.id}
                        >
                          {togglingId === w.id ? 'Guardando...' : w.is_active ? 'Desactivar' : 'Activar'}
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Almacén' : 'Nuevo Almacén'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label htmlFor="warehouse-name" className="block text-sm font-medium text-espresso-700 mb-1">
                  Nombre *
                </label>
                <input
                  id="warehouse-name"
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div>
                <label htmlFor="warehouse-address" className="block text-sm font-medium text-espresso-700 mb-1">
                  Dirección
                </label>
                <input
                  id="warehouse-address"
                  type="text"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
